import { getApiBaseUrl, getApiMode } from '../config/env';
import type {
  AuthMe,
  AuthUser,
  Session,
  Product,
  Release,
  ReleaseRow,
  ProductDistribution,
  DownloadLogItem,
  LicenseRecord,
  DeviceRecord,
  AuditLog,
  FeatureFlag,
} from '../types';

class ApiClient {
  /** In-memory only — never written to localStorage after Sprint 1. */
  private token: string | null = null;

  setToken(token: string | null) {
    this.token = token;
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    if (getApiMode() !== 'live') {
      throw new Error('Mock API mode is disabled in this production cycle. Set VITE_API_MODE=live.');
    }
    const res = await fetch(`${getApiBaseUrl()}${path}`, {
      ...init,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}),
        ...(init?.headers || {}),
      },
    });
    if (!res.ok) {
      let message = `API ${res.status}`;
      let code: string | undefined;
      let emptyReason: string | undefined;
      try {
        const body = await res.json();
        message = body.error || message;
        code = body.code;
        emptyReason = body.emptyReason;
      } catch {
        /* ignore */
      }
      const err = new Error(message) as Error & {
        status?: number;
        code?: string;
        emptyReason?: string;
      };
      err.status = res.status;
      err.code = code;
      if (emptyReason) err.emptyReason = emptyReason;
      throw err;
    }
    if (res.status === 204) return undefined as T;
    return res.json() as Promise<T>;
  }

  login(email: string, password: string) {
    return this.request<Session>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  register(body: {
    name: string;
    email: string;
    password: string;
    confirmPassword: string;
    acceptTerms: true;
  }) {
    return this.request<{
      ok: boolean;
      needsVerification: boolean;
      email?: string;
      message?: string;
      devVerifyToken?: string;
    }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  verifyEmail(token: string) {
    return this.request<{
      ok: boolean;
      email?: string;
      emailVerified: boolean;
      canContinue: boolean;
    }>('/auth/email/verify', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  resendVerification(email: string) {
    return this.request<{
      ok: boolean;
      alreadyVerified?: boolean;
      message?: string;
      devVerifyToken?: string;
    }>('/auth/email/resend-verification', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  switchOrganization(organizationId: string) {
    return this.request<AuthMe>('/auth/organizations/switch', {
      method: 'POST',
      body: JSON.stringify({ organizationId }),
    });
  }

  googleAuthStatus() {
    return this.request<{
      configured: boolean;
      publicSignupEnabled?: boolean;
      devMockAvailable?: boolean;
    }>('/auth/google/status');
  }

  completeGoogleSignIn(code: string) {
    return this.request<Session & { lang?: string; returnTo?: string; needsOnboarding?: boolean }>(
      '/auth/google/complete',
      {
        method: 'POST',
        body: JSON.stringify({ code }),
      }
    );
  }

  googleAuthorizeUrl(lang: string, returnTo = '/customer') {
    const base = getApiBaseUrl();
    const url = new URL(`${base}/auth/google`);
    url.searchParams.set('lang', lang);
    url.searchParams.set('returnTo', returnTo);
    return url.toString();
  }

  logout() {
    return this.request<void>('/auth/logout', { method: 'POST' });
  }

  me() {
    return this.request<AuthMe>('/auth/me');
  }

  securityOverview() {
    return this.request<import('../types').SecurityOverview>('/me/security/overview');
  }

  securityReauth(password: string) {
    return this.request<{ reauthToken: string; expiresAt: string }>('/me/security/reauth', {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
  }

  securityStartGoogle(input: {
    intent: 'link' | 'replace' | 'reauth';
    reauthToken?: string;
    lang?: string;
  }) {
    return this.request<{ authorizeUrl: string }>('/me/security/google/start', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  securitySetPassword(reauthToken: string, newPassword: string) {
    return this.request<import('../types').SecurityOverview>('/me/security/password', {
      method: 'POST',
      body: JSON.stringify({ reauthToken, newPassword }),
    });
  }

  securityDisconnectGoogle(reauthToken: string) {
    return this.request<import('../types').SecurityOverview>('/me/security/google/disconnect', {
      method: 'POST',
      body: JSON.stringify({ reauthToken }),
    });
  }

  securitySessions() {
    return this.request<import('../types').SecuritySession[]>('/me/security/sessions');
  }

  securityRevokeSession(id: string) {
    return this.request<void>(`/me/security/sessions/${id}`, { method: 'DELETE' });
  }

  securityRevokeAllSessions(reauthToken: string) {
    return this.request<{ revoked: number }>('/me/security/sessions/revoke-all', {
      method: 'POST',
      body: JSON.stringify({ reauthToken }),
    });
  }

  getExperience() {
    return this.request<import('../types').ExperienceState>('/me/experience');
  }

  patchExperience(body: Partial<import('../types').UserExperience> & {
    name?: string;
    complete?: boolean;
    skip?: boolean;
    resetOnboarding?: boolean;
  }) {
    return this.request<import('../types').ExperienceState>('/me/experience', {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  requestPasswordReset(email: string) {
    return this.request<{ ok: boolean; devResetToken?: string }>('/auth/password-reset/request', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  confirmPasswordReset(email: string, token: string, newPassword: string) {
    return this.request<{ ok: boolean }>('/auth/password-reset/confirm', {
      method: 'POST',
      body: JSON.stringify({ email, token, newPassword }),
    });
  }

  publicFlags() {
    return this.request<Record<string, boolean>>('/feature-flags/public');
  }

  listProducts() {
    return this.request<Product[]>('/products');
  }

  getProduct(slug: string) {
    return this.request<Product & { releases: Release[] }>(`/products/${slug}`);
  }

  adminListProducts() {
    return this.request<Product[]>('/products/admin/all');
  }

  createProduct(body: Partial<Product> & { slug: string; name: string; status: string }) {
    return this.request<Product>('/products', { method: 'POST', body: JSON.stringify(body) });
  }

  updateProduct(id: string, body: Partial<Product>) {
    return this.request<Product>(`/products/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
  }

  getProductMedia(productId: string) {
    return this.request<{
      productId: string;
      slug: string;
      name: string;
      iconUrl: string;
      heroImageUrl: string;
      videoPosterUrl: string;
      heroVideoUrl: string;
      screenshots: Array<{ src: string; alt: string; width?: number; height?: number }>;
    }>(`/products/${productId}/media`);
  }

  async uploadProductMedia(
    productId: string,
    slot: 'icon' | 'hero' | 'poster' | 'video' | 'screenshot',
    file: File,
    alt?: string,
    onProgress?: (percent: number) => void,
    replaceIndex?: number
  ) {
    const form = new FormData();
    form.append('file', file);
    form.append('slot', slot);
    if (alt) form.append('alt', alt);
    if (replaceIndex != null) form.append('replaceIndex', String(replaceIndex));

    type MediaPayload = {
      productId: string;
      slug: string;
      name: string;
      iconUrl: string;
      heroImageUrl: string;
      videoPosterUrl: string;
      heroVideoUrl: string;
      screenshots: Array<{ src: string; alt: string; width?: number; height?: number }>;
    };

    if (!onProgress) {
      const res = await fetch(`${getApiBaseUrl()}/products/${productId}/media`, {
        method: 'POST',
        credentials: 'include',
        headers: this.token ? { Authorization: `Bearer ${this.token}` } : {},
        body: form,
      });
      if (!res.ok) {
        let message = 'Upload failed';
        try {
          const body = await res.json();
          message = body.error || message;
        } catch {
          /* ignore */
        }
        throw new Error(message);
      }
      return res.json() as Promise<MediaPayload>;
    }

    return new Promise<MediaPayload>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${getApiBaseUrl()}/products/${productId}/media`);
      xhr.withCredentials = true;
      if (this.token) xhr.setRequestHeader('Authorization', `Bearer ${this.token}`);
      xhr.upload.onprogress = (event) => {
        if (!event.lengthComputable || event.total <= 0) return;
        onProgress(Math.max(0, Math.min(100, Math.round((event.loaded / event.total) * 100))));
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            onProgress(100);
            resolve(JSON.parse(xhr.responseText) as MediaPayload);
          } catch {
            reject(new Error('Upload failed'));
          }
          return;
        }
        let message = 'Upload failed';
        try {
          message = (JSON.parse(xhr.responseText) as { error?: string }).error || message;
        } catch {
          /* ignore */
        }
        reject(new Error(message));
      };
      xhr.onerror = () => reject(new Error('Upload failed'));
      xhr.send(form);
    });
  }

  deleteProductMedia(
    productId: string,
    slot: 'icon' | 'hero' | 'poster' | 'video' | 'screenshot',
    index?: number
  ) {
    const q = slot === 'screenshot' && index != null ? `?index=${index}` : '';
    return this.request<{
      productId: string;
      slug: string;
      name: string;
      iconUrl: string;
      heroImageUrl: string;
      videoPosterUrl: string;
      heroVideoUrl: string;
      screenshots: Array<{ src: string; alt: string; width?: number; height?: number }>;
    }>(`/products/${productId}/media/${slot}${q}`, { method: 'DELETE' });
  }

  reorderProductScreenshots(productId: string, order: number[]) {
    return this.request<{
      productId: string;
      slug: string;
      name: string;
      iconUrl: string;
      heroImageUrl: string;
      videoPosterUrl: string;
      heroVideoUrl: string;
      screenshots: Array<{ src: string; alt: string; width?: number; height?: number }>;
    }>(`/products/${productId}/media/screenshots`, {
      method: 'PATCH',
      body: JSON.stringify({ order }),
    });
  }

  myProducts() {
    return this.request<Product[]>('/me/products');
  }

  myLicenses() {
    return this.request<LicenseRecord[]>('/licenses/mine');
  }

  adminLicenses(params?: {
    q?: string;
    productId?: string;
    type?: string;
    status?: string;
    validity?: string;
    userId?: string;
    page?: number;
    limit?: number;
  }) {
    const qs = new URLSearchParams();
    if (params?.q) qs.set('q', params.q);
    if (params?.productId) qs.set('productId', params.productId);
    if (params?.type) qs.set('type', params.type);
    if (params?.status) qs.set('status', params.status);
    if (params?.validity) qs.set('validity', params.validity);
    if (params?.userId) qs.set('userId', params.userId);
    qs.set('page', String(params?.page || 1));
    qs.set('limit', String(params?.limit || 25));
    return this.request<{
      items: LicenseRecord[];
      total: number;
      page: number;
      limit: number;
    }>(`/licenses?${qs.toString()}`);
  }

  adminLicenseDetail(id: string) {
    return this.request<Record<string, unknown>>(`/licenses/${id}`);
  }

  updateLicenseExpiration(id: string, endsAt: string) {
    return this.request<LicenseRecord>(`/licenses/${id}/expiration`, {
      method: 'PATCH',
      body: JSON.stringify({ endsAt }),
    });
  }

  issueLicense(body: {
    userId: string;
    productId: string;
    type: string;
    seatsLimit?: number;
    endsAt?: string | null;
  }) {
    return this.request<LicenseRecord>('/licenses', { method: 'POST', body: JSON.stringify(body) });
  }

  manualLicenseSale(body: {
    productId: string;
    planId: string;
    durationDays: number | null;
    devices: number;
  }) {
    return this.request<LicenseRecord & { planId: string; licenseKey: string }>(
      '/licenses/manual-sale',
      { method: 'POST', body: JSON.stringify(body) }
    );
  }

  suspendLicense(id: string) {
    return this.request<LicenseRecord>(`/licenses/${id}/suspend`, { method: 'POST' });
  }

  activateLicense(id: string) {
    return this.request<LicenseRecord>(`/licenses/${id}/activate`, { method: 'POST' });
  }

  revokeLicense(id: string) {
    return this.request<LicenseRecord>(`/licenses/${id}/revoke`, { method: 'POST' });
  }

  productReleases(productId: string, locale?: string) {
    const qs = locale ? `?locale=${encodeURIComponent(locale)}` : '';
    return this.request<Release[]>(`/releases/product/${productId}${qs}`);
  }

  adminSearchReleases(params?: {
    q?: string;
    productId?: string;
    status?: string;
    platform?: string;
    page?: number;
    limit?: number;
  }) {
    const qs = new URLSearchParams();
    if (params?.q) qs.set('q', params.q);
    if (params?.productId) qs.set('productId', params.productId);
    if (params?.status) qs.set('status', params.status);
    if (params?.platform) qs.set('platform', params.platform);
    qs.set('page', String(params?.page || 1));
    qs.set('limit', String(params?.limit || 25));
    return this.request<{ items: ReleaseRow[]; total: number; page: number; limit: number }>(
      `/releases/admin/search?${qs.toString()}`
    );
  }

  adminProductDistribution(productId: string) {
    return this.request<ProductDistribution>(`/releases/admin/products/${productId}/distribution`);
  }

  createRelease(body: Record<string, unknown>) {
    return this.request<Release>('/releases', { method: 'POST', body: JSON.stringify(body) });
  }

  publishRelease(id: string) {
    return this.request<Release>(`/releases/${id}/publish`, { method: 'POST' });
  }

  unpublishRelease(id: string) {
    return this.request<Release>(`/releases/${id}/unpublish`, { method: 'POST' });
  }

  deleteRelease(id: string) {
    return this.request<void>(`/releases/${id}`, { method: 'DELETE' });
  }

  verifyReleaseIntegrity(id: string) {
    return this.request<{ ok: boolean; message?: string }>(`/releases/${id}/verify-integrity`, {
      method: 'POST',
    });
  }

  updateReleaseNotes(
    id: string,
    body: { changelogMd?: string; notesI18n?: { en?: string; ar?: string; ckb?: string } }
  ) {
    return this.request<Release>(`/releases/${id}/notes`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  async uploadReleaseFile(
    id: string,
    file: File,
    opts?: { replace?: boolean; onProgress?: (pct: number) => void }
  ) {
    const form = new FormData();
    form.append('file', file);
    const qs = opts?.replace ? '?replace=1' : '';
    const url = `${getApiBaseUrl()}/releases/${id}/upload${qs}`;

    if (!opts?.onProgress) {
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: this.token ? { Authorization: `Bearer ${this.token}` } : {},
        body: form,
      });
      if (!res.ok) {
        let message = 'Upload failed';
        try {
          const body = await res.json();
          message = body.error || message;
        } catch {
          /* ignore */
        }
        throw new Error(message);
      }
      return res.json() as Promise<Release>;
    }

    return new Promise<Release>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url);
      xhr.withCredentials = true;
      if (this.token) xhr.setRequestHeader('Authorization', `Bearer ${this.token}`);
      xhr.upload.onprogress = (event) => {
        if (!event.lengthComputable || event.total <= 0) return;
        opts.onProgress!(Math.max(0, Math.min(100, Math.round((event.loaded / event.total) * 100))));
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            opts.onProgress!(100);
            resolve(JSON.parse(xhr.responseText) as Release);
          } catch {
            reject(new Error('Upload failed'));
          }
          return;
        }
        let message = 'Upload failed';
        try {
          const body = JSON.parse(xhr.responseText);
          message = body.error || message;
        } catch {
          /* ignore */
        }
        reject(new Error(message));
      };
      xhr.onerror = () => reject(new Error('Upload failed'));
      xhr.send(form);
    });
  }

  downloadToken(releaseId: string) {
    return this.request<{
      downloadUrl: string;
      expiresInSec: number;
      fileName?: string;
      checksumSha256?: string;
      fileSizeBytes?: number;
    }>(`/releases/${releaseId}/download-token`, { method: 'POST' });
  }

  myDevices() {
    return this.request<DeviceRecord[]>('/devices/mine');
  }

  adminDevices(params?: {
    q?: string;
    productId?: string;
    status?: string;
    licenseId?: string;
    userId?: string;
    platform?: string;
    page?: number;
    limit?: number;
  }) {
    const qs = new URLSearchParams();
    if (params?.q) qs.set('q', params.q);
    if (params?.productId) qs.set('productId', params.productId);
    if (params?.status) qs.set('status', params.status);
    if (params?.licenseId) qs.set('licenseId', params.licenseId);
    if (params?.userId) qs.set('userId', params.userId);
    if (params?.platform) qs.set('platform', params.platform);
    qs.set('page', String(params?.page || 1));
    qs.set('limit', String(params?.limit || 25));
    return this.request<{
      items: DeviceRecord[];
      total: number;
      page: number;
      limit: number;
    }>(`/devices?${qs.toString()}`);
  }

  registerDevice(body: {
    licenseId: string;
    deviceName: string;
    platform: string;
    appVersion?: string;
    deviceFingerprint?: string;
  }) {
    return this.request<DeviceRecord>('/devices/register', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  deactivateDevice(id: string) {
    return this.request<DeviceRecord>(`/devices/${id}/deactivate`, { method: 'POST' });
  }

  adminCustomers() {
    return this.request<{
      items: Array<AuthUser & { authProvider?: string; emailVerified?: boolean; createdAt?: string }>;
      total: number;
      page: number;
      limit: number;
    }>('/admin/customers?limit=100').then((r) => r.items);
  }

  adminCustomersSearch(params: {
    q?: string;
    status?: string;
    provider?: string;
    page?: number;
    limit?: number;
  }) {
    const qs = new URLSearchParams();
    if (params.q) qs.set('q', params.q);
    if (params.status) qs.set('status', params.status);
    if (params.provider) qs.set('provider', params.provider);
    qs.set('page', String(params.page || 1));
    qs.set('limit', String(params.limit || 25));
    return this.request<{
      items: Array<AuthUser & { authProvider?: string; emailVerified?: boolean; createdAt?: string }>;
      total: number;
      page: number;
      limit: number;
    }>(`/admin/customers?${qs.toString()}`);
  }

  adminCustomer360(id: string) {
    return this.request<Record<string, unknown>>(`/admin/customers/${id}`);
  }

  createCustomerNote(customerId: string, body: string) {
    return this.request(`/admin/customers/${customerId}/notes`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    });
  }

  updateCustomerNote(customerId: string, noteId: string, body: string) {
    return this.request(`/admin/customers/${customerId}/notes/${noteId}`, {
      method: 'PATCH',
      body: JSON.stringify({ body }),
    });
  }

  getPlatformSettings() {
    return this.request<Record<string, string>>('/admin/platform-settings');
  }

  updatePlatformSettings(body: Record<string, string>) {
    return this.request<Record<string, string>>('/admin/platform-settings', {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  publicPlatformSettings() {
    return this.request<Record<string, string>>('/public/platform-settings');
  }

  createCustomer(body: { email: string; name: string; password: string }) {
    return this.request<AuthUser>('/admin/customers', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  suspendCustomer(id: string) {
    return this.request(`/admin/customers/${id}/suspend`, { method: 'POST' });
  }

  activateCustomer(id: string) {
    return this.request(`/admin/customers/${id}/activate`, { method: 'POST' });
  }

  auditLogs() {
    return this.request<AuditLog[]>('/admin/audit-logs');
  }

  downloadLogsSearch(params?: {
    q?: string;
    productId?: string;
    from?: string;
    to?: string;
    page?: number;
    limit?: number;
  }) {
    const qs = new URLSearchParams();
    if (params?.q) qs.set('q', params.q);
    if (params?.productId) qs.set('productId', params.productId);
    if (params?.from) qs.set('from', params.from);
    if (params?.to) qs.set('to', params.to);
    qs.set('page', String(params?.page || 1));
    qs.set('limit', String(params?.limit || 25));
    return this.request<{ items: DownloadLogItem[]; total: number; page: number; limit: number }>(
      `/admin/download-logs?${qs.toString()}`
    );
  }

  /** @deprecated Prefer downloadLogsSearch — kept for legacy callers expecting a flat array. */
  async downloadLogs() {
    const res = await this.downloadLogsSearch({ limit: 200, page: 1 });
    return res.items as unknown as AuditLog[];
  }

  featureFlags() {
    return this.request<FeatureFlag[]>('/admin/feature-flags');
  }

  setFeatureFlag(key: string, enabled: boolean) {
    return this.request<FeatureFlag>(`/admin/feature-flags/${key}`, {
      method: 'PATCH',
      body: JSON.stringify({ enabled }),
    });
  }

  productPlans(slug: string) {
    return this.request<{
      product: { id: string; slug: string; name: string };
      plans: Array<{
        id: string;
        code: string;
        name: string;
        period: string;
        amountMinor: number;
        currency: string;
        market?: 'iraq' | 'international';
        features: string[];
        highlight: boolean;
        displayAmount?: string;
        isLifetime?: boolean;
        isCloud?: boolean;
      }>;
      markets?: {
        iraq: Array<Record<string, unknown>>;
        international: Array<Record<string, unknown>>;
      };
      savingsPercent?: number | null;
      currency?: string;
      billingEnabled: boolean;
      fastPayConfigured: boolean;
      iraqCheckoutAvailable?: boolean;
      internationalCheckoutAvailable?: boolean;
    }>(`/hub/products/${slug}/plans`);
  }

  myEntitlements() {
    return this.request<{
      organizationId: string;
      entitlements: Array<{
        productId: string;
        productSlug: string;
        productName: string;
        entitled: boolean;
        source: string;
        status: string;
        trialEndsAt: string | null;
        launchBaseUrl: string;
      }>;
    }>('/hub/me/entitlements');
  }

  myHubSubscriptions() {
    return this.request<{
      organizationId: string;
      subscriptions: unknown[];
      trials: unknown[];
    }>('/hub/me/subscriptions');
  }

  myInvoices() {
    return this.request<{ organizationId: string; invoices: unknown[] }>('/hub/me/invoices');
  }

  startTrial(body: { productSlug: string; organizationName?: string }) {
    return this.request<{
      trial: {
        id: string;
        endsAt: string;
        status: string;
        trialDays?: number;
        startsAt?: string;
        trialStartedAt?: string;
        trialEndsAt?: string;
      };
      product: { id: string; slug: string; name: string };
      organizationId: string;
      created: boolean;
      lifecycle?: string;
      noPaymentRequired?: boolean;
    }>('/hub/trials/start', { method: 'POST', body: JSON.stringify(body) });
  }

  getTrialLifecycle(productSlug: string) {
    return this.request<{
      organizationId: string;
      product: { id: string; slug: string; name: string };
      lifecycle: string;
      trialDaysConfigured: number;
      daysRemaining: number | null;
      trialEnabled: boolean;
      trialStartedAt: string | null;
      trialEndsAt: string | null;
      dataChoiceStatus: string | null;
      startFreshAvailable: boolean;
      noPaymentRequired: boolean;
    }>(`/hub/me/trials/${encodeURIComponent(productSlug)}`);
  }

  submitTrialDataChoice(productSlug: string, choice: 'keep' | 'start_fresh') {
    return this.request<{
      organizationId: string;
      product: { id: string; slug: string; name: string };
      status: string;
      message: string;
      resetExecuted: boolean;
      clearOfflineCachesHint?: boolean;
      error?: string;
      code?: string;
    }>(`/hub/me/trials/${encodeURIComponent(productSlug)}/data-choice`, {
      method: 'POST',
      body: JSON.stringify({ choice }),
    });
  }

  createCheckoutSession(body: {
    productSlug: string;
    planCode: string;
    market?: 'iraq' | 'international';
    successUrl?: string;
    cancelUrl?: string;
  }) {
    return this.request<{
      paymentId: string;
      checkoutUrl: string;
      provider: string;
      blocker: string | null;
      redirectMessage?: string;
      amountMinor?: number;
      currency?: string;
    }>('/hub/billing/checkout-session', { method: 'POST', body: JSON.stringify(body) });
  }

  getPayment(paymentId: string) {
    return this.request<{
      paymentId: string;
      status: string;
      activated: boolean;
      amountMinor: number;
      currency: string;
      productSlug: string | null;
      paidAt?: string | null;
      activatedAt?: string | null;
    }>(`/hub/billing/payments/${paymentId}`);
  }

  getPaymentByOrder(orderId: string) {
    return this.request<{
      paymentId: string;
      status: string;
      activated: boolean;
      amountMinor: number;
      currency: string;
      productSlug: string | null;
      externalPaymentId?: string | null;
      paidAt?: string | null;
      activatedAt?: string | null;
    }>(`/hub/billing/payments/by-order/${encodeURIComponent(orderId)}`);
  }

  confirmPayment(paymentId: string) {
    return this.request<{
      paymentId: string;
      status: string;
      activated: boolean;
      alreadyActivated?: boolean;
      pending?: boolean;
    }>(`/hub/billing/payments/${paymentId}/confirm`, { method: 'POST' });
  }

  adminPayments(params?: { status?: string; limit?: number }) {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.limit) qs.set('limit', String(params.limit));
    const q = qs.toString();
    return this.request<{
      items: Array<{
        id: string;
        organizationId: string;
        status: string;
        amountMinor: number;
        currency: string;
        provider: string;
        externalPaymentId: string | null;
        providerTransactionId: string | null;
        productSlug: string | null;
        planCode: string | null;
        paidAt: string | null;
        activatedAt: string | null;
        createdAt: string;
      }>;
    }>(`/hub/admin/payments${q ? `?${q}` : ''}`);
  }

  adminReconcilePayment(id: string) {
    return this.request<{
      paymentId: string;
      status: string;
      activated: boolean;
      alreadyActivated?: boolean;
      providerStatus?: string;
    }>(`/hub/admin/payments/${id}/reconcile`, { method: 'POST' });
  }

  mintLaunch(body: { productSlug: string; returnUrl?: string }) {
    return this.request<{
      code: string;
      expiresAt: string;
      redirectUrl: string | null;
      softLaunchPath: string | null;
      entitlementSource: string;
    }>('/launch', { method: 'POST', body: JSON.stringify(body) });
  }

  entitlementsSummary() {
    return this.request<{
      licenses: Record<string, number>;
      trials: Record<string, number>;
      subscriptions: Record<string, number>;
      devices: Record<string, number>;
    }>('/admin/entitlements/summary');
  }

  adminTrials(params?: {
    q?: string;
    productId?: string;
    status?: string;
    organizationId?: string;
    page?: number;
    limit?: number;
  }) {
    const qs = new URLSearchParams();
    if (params?.q) qs.set('q', params.q);
    if (params?.productId) qs.set('productId', params.productId);
    if (params?.status) qs.set('status', params.status);
    if (params?.organizationId) qs.set('organizationId', params.organizationId);
    qs.set('page', String(params?.page || 1));
    qs.set('limit', String(params?.limit || 25));
    return this.request<{
      items: Array<{
        id: string;
        organizationId: string;
        status: string;
        effectiveStatus?: string;
        startsAt: string;
        endsAt: string;
        createdAt: string;
        daysRemaining?: number;
        source?: string;
        product: { id: string; slug: string; name: string };
        organizationName?: string | null;
        customer?: { id: string; name: string; email: string } | null;
      }>;
      total: number;
      page: number;
      limit: number;
    }>(`/hub/admin/trials?${qs.toString()}`);
  }

  extendTrial(
    id: string,
    body: { endsAt?: string; extendDays?: number; forceGrant?: boolean }
  ) {
    return this.request(`/hub/admin/trials/${id}/extend`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  cancelTrial(id: string) {
    return this.request(`/hub/admin/trials/${id}/cancel`, { method: 'POST' });
  }

  adminSubscriptions(params?: {
    q?: string;
    productId?: string;
    status?: string;
    period?: string;
    provider?: string;
    organizationId?: string;
    page?: number;
    limit?: number;
  }) {
    const qs = new URLSearchParams();
    if (params?.q) qs.set('q', params.q);
    if (params?.productId) qs.set('productId', params.productId);
    if (params?.status) qs.set('status', params.status);
    if (params?.period) qs.set('period', params.period);
    if (params?.provider) qs.set('provider', params.provider);
    if (params?.organizationId) qs.set('organizationId', params.organizationId);
    qs.set('page', String(params?.page || 1));
    qs.set('limit', String(params?.limit || 25));
    return this.request<{
      items: Array<{
        id: string;
        organizationId: string;
        status: string;
        provider: string | null;
        currentPeriodStart: string | null;
        currentPeriodEnd: string | null;
        product: { id: string; slug: string; name: string };
        plan: { id: string; code: string; name: string; period?: string } | null;
        organizationName?: string | null;
        customer?: { id: string; name: string; email: string } | null;
        latestInvoice?: {
          id: string;
          status: string;
          amountMinor: number;
          currency: string;
          createdAt: string;
          paidAt?: string | null;
        } | null;
        latestPayment?: {
          id: string;
          status: string;
          provider: string;
          amountMinor: number;
          currency: string;
          createdAt: string;
        } | null;
      }>;
      total: number;
      page: number;
      limit: number;
    }>(`/hub/admin/subscriptions?${qs.toString()}`);
  }

  activateSubscription(id: string) {
    return this.request(`/hub/admin/subscriptions/${id}/activate`, { method: 'POST' });
  }

  setLaunchBaseUrl(productId: string, launchBaseUrl: string) {
    return this.request<{ id: string; slug: string; launchBaseUrl: string }>(
      `/hub/admin/products/${productId}/launch-base-url`,
      { method: 'PUT', body: JSON.stringify({ launchBaseUrl }) }
    );
  }

  getPublicProductCommercial(slug: string, locale: string) {
    const qs = new URLSearchParams({ locale });
    return this.request<Record<string, unknown>>(`/public/products/${slug}/commercial?${qs}`);
  }

  adminProductCommercial(id: string) {
    return this.request<Record<string, unknown>>(`/admin/products/${id}/commercial`);
  }

  adminSaveCommercialFlags(
    id: string,
    body: {
      trialDays?: number;
      trialEnabled?: boolean;
      operationalTrial?: boolean;
      purchasable?: boolean;
    }
  ) {
    return this.request<Record<string, unknown>>(`/admin/products/${id}/commercial/flags`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  adminSaveCommercialLocale(id: string, locale: string, body: Record<string, unknown>) {
    return this.request<Record<string, unknown>>(`/admin/products/${id}/commercial/locales/${locale}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  adminPublishCommercial(id: string) {
    return this.request<Record<string, unknown>>(`/admin/products/${id}/commercial/publish`, {
      method: 'POST',
    });
  }

  adminPatchProductCore(
    id: string,
    body: { name?: string; status?: string; launchBaseUrl?: string }
  ) {
    return this.request<Record<string, unknown>>(`/admin/products/${id}/core`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  adminUpdatePlanPrice(planId: string, amountMinor: number) {
    return this.request<Record<string, unknown>>(`/admin/plans/${planId}/price`, {
      method: 'PATCH',
      body: JSON.stringify({ amountMinor }),
    });
  }

  adminPatchPlan(planId: string, body: { amountMinor?: number; active?: boolean }) {
    return this.request<Record<string, unknown>>(`/admin/plans/${planId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  adminCreatePlan(
    productId: string,
    body: {
      code: 'monthly' | 'annual' | 'lifetime' | 'cloud_annual';
      market: 'iraq' | 'international';
      amountMinor: number;
      active?: boolean;
    }
  ) {
    return this.request<Record<string, unknown>>(`/admin/products/${productId}/plans`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  adminGetCommercialPolicy(locale: string) {
    const qs = new URLSearchParams({ locale });
    return this.request<Record<string, unknown> | null>(`/admin/commercial-policy?${qs}`);
  }

  adminSaveCommercialPolicy(body: Record<string, unknown>) {
    return this.request<Record<string, unknown>>('/admin/commercial-policy', {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  adminListCommercialFaqs(productId?: string | null) {
    const qs = productId ? `?productId=${encodeURIComponent(productId)}` : '';
    return this.request<
      Array<{
        id: string;
        productId: string | null;
        sortOrder: number;
        active: boolean;
        locales?: Record<string, { question: string; answer: string }>;
      }>
    >(`/admin/commercial-faq${qs}`);
  }

  adminCreateCommercialFaq(body: Record<string, unknown>) {
    return this.request<{ id: string }>('/admin/commercial-faq', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  adminUpdateCommercialFaq(id: string, body: Record<string, unknown>) {
    return this.request<{ ok: boolean }>(`/admin/commercial-faq/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  adminDeleteCommercialFaq(id: string) {
    return this.request<void>(`/admin/commercial-faq/${id}`, { method: 'DELETE' });
  }

  ssoStatus() {
    return this.request<{ googleOAuth: boolean; samlFoundation: boolean; samlProductionLogin: boolean }>(
      '/auth/sso/status'
    );
  }

  health() {
    return this.request<{ status: string }>('/health');
  }
}

export const api = new ApiClient();
